// ============================================================================
// API Client — handles all backend communication with auth token injection
// ============================================================================

import { config } from './config';
import type {
  AppUser,
  CaptionTemplate,
  ContentItem,
  Draft,
  GenerationStatus,
  Idea,
  ImageGeneration,
  IdeaConfig,
  JobStatus,
  PaginatedResponse,
  PlatformConfig,
  TranscriptSegment,
  UploadUrlResponse,
  SourceAsset,
} from '../types';

type TokenProvider = () => Promise<string | null>;

class ApiClient {
  private baseUrl: string;
  private getToken: TokenProvider | null = null;
  private onAuthFailure: (() => void) | null = null;

  private useRawJwt(path: string): boolean {
    return path.startsWith('/drafts') || path.startsWith('/platforms');
  }

  constructor() {
    this.baseUrl = config.apiUrl;
  }

  /**
   * Set the async token provider (from AuthProvider).
   * This function is called before every API request to get a fresh token.
   */
  setTokenProvider(fn: TokenProvider) {
    this.getToken = fn;
  }

  /**
   * Set a callback for when auth permanently fails (refresh token expired).
   * Typically redirects to login.
   */
  setAuthFailureHandler(fn: () => void) {
    this.onAuthFailure = fn;
  }

  private async request<T>(
    path: string,
    options: RequestInit = {},
    isRetry = false,
  ): Promise<T> {
    const token = await this.getToken?.();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: this.useRawJwt(path) ? token : `Bearer ${token}` } : {}),
      ...((options.headers as Record<string, string>) || {}),
    };

    const response = await fetch(`${this.baseUrl}${path}`, {
      ...options,
      headers,
    });

    // On 401: try refreshing the token and retrying once
    if (response.status === 401 && !isRetry) {
      // Force a fresh token (the provider handles refresh)
      const freshToken = await this.getToken?.();
      if (freshToken && freshToken !== token) {
        // Token was successfully refreshed — retry the request
        return this.request<T>(path, options, true);
      }

      // Token refresh failed — session is dead
      this.onAuthFailure?.();
      throw new ApiError(401, 'Session expired. Please log in again.');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        message: response.statusText,
      }));
      throw new ApiError(
        response.status,
        error.message || 'Request failed',
        error,
      );
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  // ── Content Endpoints ───────────────────────────────────────────

  async generateUploadUrl(
    filename: string,
    contentType: string,
    fileSize: number,
  ): Promise<UploadUrlResponse> {
    return this.request<UploadUrlResponse>('/content/upload-url', {
      method: 'POST',
      body: JSON.stringify({ filename, contentType, fileSize }),
    });
  }

  async createContent(data: {
    title: string;
    description?: string;
    s3Key?: string;
    s3Keys?: string[];
    sourceAssets?: SourceAsset[];
    mediaType: 'video' | 'image';
    filename?: string;
    fileSize?: number;
    metadata?: Record<string, unknown>;
  }): Promise<ContentItem> {
    return this.request<ContentItem>('/content', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async listContent(params?: {
    limit?: number;
    nextToken?: string;
    status?: string;
  }): Promise<PaginatedResponse<ContentItem>> {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.nextToken) searchParams.set('nextToken', params.nextToken);
    if (params?.status) searchParams.set('status', params.status);
    const qs = searchParams.toString();
    return this.request<PaginatedResponse<ContentItem>>(
      `/content${qs ? `?${qs}` : ''}`,
    );
  }

  async getContent(contentId: string): Promise<ContentItem & { drafts: Draft[] }> {
    return this.request<ContentItem & { drafts: Draft[] }>(
      `/content/${contentId}`,
    );
  }

  async deleteContent(contentId: string): Promise<void> {
    return this.request<void>(`/content/${contentId}`, {
      method: 'DELETE',
    });
  }

  async updateTranscript(
    contentId: string,
    data: { transcript: string; segments?: TranscriptSegment[] },
  ): Promise<{ message: string; content: ContentItem }> {
    return this.request(`/content/${contentId}/transcript`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async approveTranscript(contentId: string, data: { transcript: string; segments?: TranscriptSegment[] }): Promise<{ message: string; content: ContentItem; batchJobId?: string }> {
    return this.request(`/content/${contentId}/transcript`, {
      method: 'PUT',
      body: JSON.stringify({ ...data, approve: true }),
    });
  }

  // ── Draft Endpoints ─────────────────────────────────────────────

  async listDrafts(params?: {
    limit?: number;
    nextToken?: string;
    platform?: string;
    status?: string;
    contentId?: string;
  }): Promise<PaginatedResponse<Draft>> {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.nextToken) searchParams.set('nextToken', params.nextToken);
    if (params?.platform) searchParams.set('platform', params.platform);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.contentId) searchParams.set('contentId', params.contentId);
    const qs = searchParams.toString();
    return this.request<PaginatedResponse<Draft>>(
      `/drafts${qs ? `?${qs}` : ''}`,
    );
  }

  async createDraft(data: {
    contentId: string;
    platform: string;
    textContent: string;
    mediaUrls?: string[];
    platformMetadata?: Record<string, unknown>;
  }): Promise<Draft> {
    return this.request<Draft>('/drafts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateDraft(
    draftId: string,
    data: {
      textContent?: string;
      mediaUrls?: string[];
      platformMetadata?: Record<string, unknown>;
    },
  ): Promise<Draft> {
    return this.request<Draft>(`/drafts/${draftId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async approveDraft(draftId: string): Promise<Draft> {
    return this.request<Draft>(`/drafts/${draftId}/approve`, {
      method: 'POST',
    });
  }

  async rejectDraft(draftId: string, notes: string): Promise<Draft> {
    return this.request<Draft>(`/drafts/${draftId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
  }

  async scheduleDraft(
    draftId: string,
    scheduledAt: string,
  ): Promise<Draft> {
    return this.request<Draft>(`/drafts/${draftId}/schedule`, {
      method: 'POST',
      body: JSON.stringify({ scheduledAt }),
    });
  }

  async deleteDraft(draftId: string): Promise<void> {
    return this.request<void>(`/drafts/${draftId}`, {
      method: 'DELETE',
    });
  }

  // ── Content Status & Download ─────────────────────────────────

  async getDownloadUrl(contentId: string): Promise<{
    contentId: string;
    title: string;
    mediaType: string;
    sourceUrl: string;
    downloadUrl: string;
    thumbnailDownloadUrl?: string;
    expiresIn: number;
    metadata: Record<string, unknown>;
  }> {
    return this.request(`/content/${contentId}/download-url`);
  }

  async updateContentStatus(
    contentId: string,
    data: {
      status?: string;
      duration?: number;
      thumbnailUrl?: string;
      metadata?: Record<string, unknown>;
      processingNotes?: string;
    },
  ): Promise<Record<string, unknown>> {
    return this.request(`/content/${contentId}/status`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // ── Platform Endpoints ──────────────────────────────────────────

  async listPlatforms(): Promise<{ platforms: PlatformConfig[] }> {
    return this.request<{ platforms: PlatformConfig[] }>('/platforms');
  }

  // ── Caption Templates ───────────────────────────────────────────

  async listCaptionTemplates(): Promise<{ templates: CaptionTemplate[]; cached?: boolean }> {
    return this.request<{ templates: CaptionTemplate[]; cached?: boolean }>('/caption-templates');
  }

  // ── Connection Endpoints ────────────────────────────────────────

  async getConnections(): Promise<{
    connections: Array<{
      platform: string;
      status: 'connected' | 'not_connected' | 'expired';
      accountName?: string;
      connectedAt?: string;
      connectedBy?: string;
    }>;
  }> {
    return this.request('/platforms/connections');
  }

  async disconnectPlatform(platform: string): Promise<void> {
    return this.request(`/platforms/${platform}/disconnect`, {
      method: 'DELETE',
    });
  }

  // ── Credentials Endpoints ─────────────────────────────────────────

  async getCredentialsStatus(): Promise<{
    platforms: Array<{
      platform: string;
      configured: boolean;
      clientIdSet: boolean;
      clientSecretSet: boolean;
    }>;
  }> {
    return this.request('/platforms/credentials');
  }

  async updateCredentials(
    platform: string,
    clientId: string,
    clientSecret: string,
  ): Promise<{ platform: string; configured: boolean }> {
    return this.request(`/platforms/${platform}/credentials`, {
      method: 'PUT',
      body: JSON.stringify({ clientId, clientSecret }),
    });
  }

  async clearCredentials(platform: string): Promise<{ platform: string; configured: boolean }> {
    return this.request(`/platforms/${platform}/credentials`, {
      method: 'DELETE',
    });
  }

  // ── Batch Job Status Endpoints ──────────────────────────────────

  async getJobStatuses(contentId: string): Promise<{ jobs: JobStatus[] }> {
    return this.request<{ jobs: JobStatus[] }>(
      `/content/${contentId}/jobs`,
    );
  }

  async retryJob(contentId: string, jobType: string): Promise<{
    message: string;
    batchJobId: string;
    jobType: string;
    content: ContentItem;
  }> {
    return this.request(`/content/${contentId}/jobs/${jobType}/retry`, {
      method: 'POST',
    });
  }

  // ── Settings / Secrets Endpoints ────────────────────────────────

  async getSecretsStatus(): Promise<{
    kilo: { configured: boolean; maskedKey?: string };
    captions_ai: { configured: boolean; maskedKey?: string };
  }> {
    return this.request('/settings/secrets/status');
  }

  async updateKiloKey(apiKey: string): Promise<{
    message: string;
    configured: boolean;
    maskedKey: string;
  }> {
    return this.request('/settings/secrets/kilo', {
      method: 'PUT',
      body: JSON.stringify({ apiKey }),
    });
  }

  async updateCaptionsAiKey(apiKey: string): Promise<{
    message: string;
    configured: boolean;
    maskedKey: string;
  }> {
    return this.request('/settings/secrets/captions-ai', {
      method: 'PUT',
      body: JSON.stringify({ apiKey }),
    });
  }

  // ── Settings / Preferences Endpoints ──────────────────────────────

  async getPreferences(): Promise<{
    captionEngine: 'captions_ai' | 'ffmpeg';
    captionTemplateId: string;
  }> {
    return this.request('/settings/preferences');
  }

  async updatePreferences(data: {
    captionEngine: 'captions_ai' | 'ffmpeg';
    captionTemplateId?: string;
  }): Promise<{
    captionEngine: string;
    captionTemplateId: string;
    message: string;
  }> {
    return this.request('/settings/preferences', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // ── Ideas Endpoints ─────────────────────────────────────────────

  async listIdeas(params?: {
    category?: string;
    status?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
    limit?: number;
    nextToken?: string;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<PaginatedResponse<Idea>> {
    const searchParams = new URLSearchParams();
    if (params?.category) searchParams.set('category', params.category);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder);
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.nextToken) searchParams.set('nextToken', params.nextToken);
    if (params?.dateFrom) searchParams.set('dateFrom', params.dateFrom);
    if (params?.dateTo) searchParams.set('dateTo', params.dateTo);
    const qs = searchParams.toString();
    return this.request<PaginatedResponse<Idea>>(
      `/ideas${qs ? `?${qs}` : ''}`,
    );
  }

  async getIdea(ideaId: string): Promise<Idea> {
    return this.request<Idea>(`/ideas/${ideaId}`);
  }

  async updateIdea(
    ideaId: string,
    data: { status?: string; title?: string; description?: string; category?: string; tags?: string[] },
  ): Promise<Idea> {
    return this.request<Idea>(`/ideas/${ideaId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteIdea(ideaId: string): Promise<void> {
    return this.request<void>(`/ideas/${ideaId}`, {
      method: 'DELETE',
    });
  }

  async triggerIdeaGeneration(params?: {
    focus?: string;
    count?: number;
  }): Promise<{ generationId: string; status: string }> {
    return this.request('/ideas/generate', {
      method: 'POST',
      body: JSON.stringify(params || {}),
    });
  }

  async getGenerationStatus(): Promise<GenerationStatus> {
    return this.request<GenerationStatus>('/ideas/generation-status');
  }

  async listModels(): Promise<{ models: { id: string; name: string }[] }> {
    return this.request<{ models: { id: string; name: string }[] }>('/ideas/models');
  }

  async listImageGenerations(): Promise<{ items: ImageGeneration[] }> {
    return this.request('/images');
  }

  async getImageGeneration(imageGenerationId: string): Promise<ImageGeneration> {
    return this.request(`/images/${imageGenerationId}`);
  }

  async generateImageUploadUrl(
    filename: string,
    contentType: string,
    fileSize: number,
  ): Promise<UploadUrlResponse> {
    return this.request('/images/upload-url', {
      method: 'POST',
      body: JSON.stringify({ filename, contentType, fileSize }),
    });
  }

  async triggerImageGeneration(data: {
    prompt: string;
    mode: 'image_only' | 'text_plus_image';
    aspectRatio: string;
    size: string;
    count: number;
    inputImages: Array<{ s3Key: string; filename?: string; contentType?: string }>;
  }): Promise<{ imageGenerationId: string; status: string }> {
    return this.request('/images/generate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getIdeaConfig(): Promise<IdeaConfig> {
    return this.request<IdeaConfig>('/ideas/config');
  }

  async updateIdeaConfig(data: Partial<IdeaConfig>): Promise<IdeaConfig> {
    return this.request<IdeaConfig>('/ideas/config', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // ── User Management Endpoints (admin only) ─────────────────────

  async listUsers(): Promise<{ users: AppUser[] }> {
    return this.request<{ users: AppUser[] }>('/users');
  }

  async createUser(data: {
    email: string;
    fullName: string;
    role: 'admin' | 'editor';
    tempPassword?: string;
  }): Promise<AppUser & { tempPassword: string }> {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUser(
    username: string,
    data: {
      action: 'enable' | 'disable' | 'changeRole';
      role?: 'admin' | 'editor';
    },
  ): Promise<{ message: string; username: string; role?: string }> {
    return this.request(`/users/${encodeURIComponent(username)}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async resetUserPassword(
    username: string,
    data: {
      action: 'reset' | 'setPassword';
      newPassword?: string;
      permanent?: boolean;
    },
  ): Promise<{ message: string; username: string }> {
    return this.request(`/users/${encodeURIComponent(username)}/reset-password`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Singleton
export const api = new ApiClient();
